import React, { useState, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useAuth } from "providers/Auth";
import { useConfig } from "providers/Config";
import { useGlobalConfig } from "providers/GlobalConfig";
import FullPageLoader from "UIComponents/FullPageLoader";
import { AuthStatus } from "types/auth";
import { auth as firebaseAuth } from "firebaseClient";

function AuthorizedRoute({ children }) {
  const { appConfig } = useConfig();
  const { auth: authConfig } = appConfig || {};
  const { signInURL = "/signin" } = authConfig || {};

  const { auth, needsTenantSelection, refreshClaims } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const { globalConfig, loadConfig } = useGlobalConfig() || {};
  const { configReady } = globalConfig || {};
  const configLoadKeyRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (auth.status !== AuthStatus.READY) return;
    if (!auth.isAuthenticated) return;
    if (!auth.tenant) return;
    if (configReady) return;
    if (!loadConfig) return;

    const u = firebaseAuth.currentUser;

    if (!u) return;
    const key = `${auth.tenant}::${u.uid}`;

    if (configLoadKeyRef.current === key) return;
    configLoadKeyRef.current = key;

    void loadConfig(u, auth.tenant);
  }, [auth.status, auth.isAuthenticated, auth.tenant, configReady, loadConfig]);

  if (
    auth.status === AuthStatus.INITIALIZING ||
    auth.status === AuthStatus.LOADING_CLAIMS
  ) {
    return <FullPageLoader />;
  }

  if (auth.status === AuthStatus.SIGNED_OUT) {
    return (
      <Navigate
        to={{
          pathname: signInURL,
          search: `from=${location.pathname}`,
          state: { from: location },
        }}
        replace
      />
    );
  }

  if (needsTenantSelection) {
    return <Navigate to="/select-tenant" state={{ from: location }} replace />;
  }

  if (auth.status === AuthStatus.ERROR) {
    const handleRetry = async () => {
      setRetrying(true);
      try {
        await refreshClaims({ forceRefresh: true });
      } finally {
        setRetrying(false);
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="text.primary">
          Authentication Error
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          We encountered a problem.
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRetry}
          disabled={retrying}
          sx={{ mt: 1 }}
        >
          {retrying ? "Retrying..." : "Try Again"}
        </Button>
      </Box>
    );
  }

  if (auth.status === AuthStatus.READY) {
    if (auth.tenant && !configReady) {
      return <FullPageLoader />;
    }
    return children;
  }

  // Safety net
  return <FullPageLoader />;
}

export default AuthorizedRoute;
